import { CustomFieldType, CustomLabelField } from './invites';
import { C2BOrderOutDto } from './marketplace';

export type Event = {
  id: number;
  communityId: number;
  title: string;
  memberCount: number;
  description: string;
  location: string;
  locationUrl?: string;
  onlineLocation: boolean;
  startTime: Date;
  endTime?: Date;
  deadline?: Date;
  maximumAttendees?: number;
  picture?: string;
  notificationSettings?: NotificationSettings;
  type: EventType;
  isCancelled: boolean;
  organizer: string;
  role: EventRole;
  participants: EventParticipant[];
  dateOptions: DateOption[];
  isRecurring: boolean;
  frequency: Frequency;
  isDatePicker: boolean;
  recurrenceBatchId: string;
  externalCalendarUrl?: string;
  customFields: EventCustomField[];
  attendanceRequired: boolean;
  priceOfTicket?: number;
};

export type PersonalEvent = {
  id: number;
  businessId: number;
  title: string;
  memberCount: number;
  description: string;
  location: string;
  locationUrl: string;
  startTime: Date;
  endTime: Date;
  deadline?: Date;
  type: EventType;
  isCancelled: boolean;
  organizer: string;
  role: EventRole;
  ownerId: number;
  picture: string;
  participants: EventParticipant[];
  maximumAttendees?: number;
  dateOptions: DateOption[];
  isRecurring: boolean;
  frequency: Frequency;
  isDatePicker: boolean;
  isPicked: boolean;
  recurrenceBatchId: string;
  notificationSettings: NotificationSettings;
  customFields: EventCustomField[];
  recurrenceEndDate?: Date;
  priceOfTicket?: number;
  eventTicketUnit?: C2BOrderOutDto;
  /**
   * Email address the external invite was sent to (only present on invite-based responses)
   */
  invitedEmail?: string | null;
};

export enum EventCustomFieldType {
  TEXT = CustomFieldType.TEXT,
  MULTIPLE_CHOICE = CustomFieldType.MULTIPLE_CHOICE,
}

export type EventCustomField = CustomLabelField & {
  responses: EventCustomFieldResponse[];
};

export interface EventCustomFieldResponse {
  userId: number;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    picture: string;
  };
  response: string;
  customFieldId: number;
}

export enum Frequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export interface DateOption {
  id: number;
  startTime: Date;
  endTime?: Date;
  answers: DateOptionAnswer[];
}

export interface DateOptionAnswer {
  id: number;
  firstName: string;
  lastName: string;
  picture?: string;
  answer: EventParticipationAnswer;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

export enum EventParticipationAnswer {
  YES = 'YES',
  MAYBE = 'MAYBE',
  NO = 'NO',
  PENDING = 'PENDING',
  GOING = 'GOING',
  NOT_GOING = 'NOT_GOING',
}

export enum RecurrenceEditType {
  SINGLE = 'SINGLE',
  ALL = 'ALL',
  FUTURE = 'FUTURE',
}

export type EventParticipant = {
  id: number;
  firstName: string;
  lastName: string;
  picture: string;
  role?: EventRole;
  description: string;
  answer?: EventParticipationAnswer;
  answers?: DateOptionAnswer[];
  createdAt: Date;
  updatedAt: Date;
};

export enum EventRole {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

export enum EventType {
  SOCIAL = 'SOCIAL',
  WORK = 'WORK',
  SPORT = 'SPORT',
  OTHER = 'OTHER',
  DEFAULT = 'DEFAULT',
}

export type CreateEventModel = {
  title: string;
  description: string;
  location: string;
  locationUrl?: string;
  onlineLocation: boolean;
  startTime: Date;
  endTime?: Date;
  isRecurring?: boolean;
  frequency?: Frequency;
  deadline?: Date;
  recurrenceEndDate?: Date;
  maximumAttendees?: number;
  type: EventType;
  picture?: string;
  isDatePicker: boolean;
  notificationSettings?: NotificationSettings;
  customFields: EventCustomField[];
  dateOptions: Omit<DateOption, 'id' | 'answers'>[];
  participantsIds?: number[];
  attendanceRequired?: boolean;
  priceOfTicket?: number;
  /**
   * Email address the external invite was sent to (only present on invite-based responses)
   */
  invitedEmail?: string | null;
};

export type NotificationSettings = {
  [userType: string]: {
    [type in EventNotificationType]?: {
      setting: boolean;
    };
  };
};

export enum EventNotificationType {
  EVENT_INVITE = 'EVENT_INVITE',
  EVENT_UPDATE = 'EVENT_UPDATE',
  EVENT_CANCELLED = 'EVENT_CANCELLED',
  EVENT_ANSWER_REMIND = 'EVENT_ANSWER_REMIND',
  EVENT_PARTICIPANT_ANSWERED = 'EVENT_PARTICIPANT_ANSWERED',
  EVENT_PARTICIPANT_DECLINED = 'EVENT_PARTICIPANT_DECLINED',
  EVENT_DATEPICKER_INVITE = 'EVENT_DATEPICKER_INVITE',
  EVENT_DATEPICKER_RESPONDED = 'EVENT_DATEPICKER_RESPONDED',
  EVENT_DATEPICKER_PICKED = 'EVENT_DATEPICKER_PICKED',
}

export type UpdateEventModel = Omit<
  CreateEventModel,
  'isRecurring' | 'frequency' | 'recurrenceEndDate' | 'customFields' | 'isDatePicker'
>;

export enum EventViewType {
  FUTURE = 'FUTURE',
  PAST = 'PAST',
  ALL = 'ALL',
}

export type AttendantData = {
  id: number;
  name: string;
  yesFraction: number;
  picture?: string;
  [EventParticipationAnswer.YES]: number;
  [EventParticipationAnswer.NO]: number;
  [EventParticipationAnswer.MAYBE]: number;
  [EventParticipationAnswer.PENDING]: number;
};

export const ZERO_PLACEHOLDER = 0.0236785901;

export type DateOptionScoredWithSection = Partial<DateOptionScored> & { type: string; title: string };

export interface DateOptionScored extends DateOption {
  score: number;
  counts: { [key in EventParticipationAnswer]: number };
  resolvedAnswerCount: number;
  totalAnswers: number;
  normalizedScore: number;
  percentage: number;
}

export interface EventLinkData {
  eid: number;
}
